import { Injectable } from '@angular/core';
import { UserService } from '../user-service/user.service';
import { map, concatMap, tap, last, scan, takeWhile, catchError } from 'rxjs/operators';
import { Observable, timer, of, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ChatID } from 'src/app/models/chat-id.enum';
import { ChatMessagesHasView } from 'src/app/models/chat-messages';
import { TranslatableError, ErrorType } from '@cordic/request';
import { ChatBroadcaster } from '../chat-broadcaster/chat-broadcaster';
import { TranslateService } from '@ngx-translate/core';
import { ChatCount } from 'src/app/models/chat-count.enum';
import { ChatMailWebService, Conversation, DriverDetails, ChatMessage2, SystemVerbs } from '@cordic/chatmail-web';
import { BroadcasterEvent } from 'src/app/models/broadcaster-events.enum';
import { PredefinedMessages } from 'src/app/models/predefined-messages';
import { ChatMsgType } from 'src/app/models/chat-types.enum';
import { LocalStorageItem } from 'src/app/models/localstorage-item.enum';
import { RequestOptionsProviderService } from '../request-options-provider/request-options-provider.service';

@Injectable({
  providedIn: 'root'
})

export class ChatService {

  predefinedMessages: PredefinedMessages = new PredefinedMessages();
  private despatchGroups = [];
  private chatsFilteredByDespGroup = [];

  constructor(public userService: UserService,
              public chatMailService: ChatMailWebService,
              private broadcaster: ChatBroadcaster,
              private requestOptionsProvider: RequestOptionsProviderService,
              private translateService: TranslateService) {}
  
  getOverrideUrl(verb: SystemVerbs)
  {
    let apiUrl = this.requestOptionsProvider.apiUrl.toString()
    return apiUrl.replace('/chatmail?', '/chatmailpoll?') + verb;
  }

  /*
    Gets the messages to be displayed in the view (combined messages - from storage + new messages from server)
  */
  public getViewMessages(driverID: number, selectedContact?: Conversation | DriverDetails): Observable<ChatMessagesHasView[]>
  {
    const INT_MAX = 0x7ffffffff;
    const storagedMsgs = this.getMessagesFromStorage(driverID);
    let searchForward = true;
    let lastChatId = this.latestChatId(storagedMsgs);

    if ((lastChatId === ChatID.UNKNOWN_CHAT_ID) &&
        ((!storagedMsgs) || !(storagedMsgs.length > 0)))
    { 
      lastChatId = INT_MAX;
      searchForward = false;
    }

    return this.getRetrievedMessagesFromServer(driverID, lastChatId, searchForward).pipe(
      map(messages => [...this.getMessagesFromStorage(driverID) || [], ...messages || []].filter((val, index, arr) =>
      {
        const msgIndex = arr.findIndex(msg => msg.chatID === val.chatID);
        return msgIndex === index;
      })),
      map(combinedMessages => combinedMessages.sort((a, b) => b.chatID - a.chatID)),
      tap(messages =>
        {
          if (!messages.length) { return; }
  
          this.saveMessagesInStorage(messages.slice(0, environment.maxCacheMessageCount), driverID);
          
          this.broadcaster.broadcast(BroadcasterEvent.EVENT_ON_MESSAGE_SENT_OR_RETRIEVED,
            { message: this.lastestMessage(messages),
              toDriver: selectedContact
            });
        })
    );
  }

  public getRetrievedMessagesFromServer(driverID: number, lastChatId: number, searchForward: boolean = true)
  {
    return new Observable<ChatMessagesHasView[]>((subscriber) =>
    {  
      return timer(0, environment.messagePollingInterval).pipe(
        concatMap(() =>
        {
          return this.retrieveMessagesFromServer(lastChatId, driverID, searchForward)
          .pipe(
            catchError(error =>
            {
              return of(null);
            }) 
          );
        }),
      ).subscribe(chatMessages =>
      {
        subscriber.next(chatMessages);
        
        if ((chatMessages) &&
            (chatMessages.length))
        {
          lastChatId = this.latestChatId(chatMessages);
          searchForward = true;
        }
      });
    });
  }

  private retrieveMessagesFromServer(initialLastChatId: number, driverID: number, searchForward: boolean)
  {
    return new Observable<ChatMessagesHasView[]>(subscriber =>
    {
      const nextChatId = new BehaviorSubject(initialLastChatId);
      
      return nextChatId.pipe(
        concatMap(lastChatId =>
        {
          const chatCount = !searchForward ? ChatCount.MIN_MSGS : ChatCount.MAX_MSGS;

          return this.chatMailService.operatorRetrieve(this.userService.getJWT(), lastChatId, driverID, chatCount,
                 this.getDespatchGroups(), searchForward,
                 this.getOverrideUrl(SystemVerbs.SystemVerbOperatorRetrieve));
        }),
        map(resp =>
        {
          return resp.chat.map(message =>
                 ({...message, hasViewed: false}) as ChatMessagesHasView);
        }),
        scan((acc, messages) =>
        {
          return {accMessages: [...acc.accMessages, ...messages], curr: messages}
        }, {accMessages: [], curr: null}),
        takeWhile(({curr}) =>
        {
          return searchForward && curr.length === ChatCount.MAX_MSGS && this.latestChatId(curr) != ChatID.UNKNOWN_CHAT_ID;
        }, true),
        map(({accMessages}) => {
          return accMessages;
        }),
        tap(messages =>
        {
          nextChatId.next(this.latestChatId(messages));
        }),
        last(),
      ).subscribe(subscriber);
    });
  } 

  public earliestChatId(chatMessages: ChatMessagesHasView[])
  {
    if (!chatMessages || chatMessages.length === 0) { return 0; }
    return Math.min(...chatMessages.map(message => message.chatID));
  }


  public lastestMessage(chatMessages: ChatMessage2[]): ChatMessage2
  {
    if (!chatMessages)
    {
      return;
    }

    return chatMessages.find(x => x.chatID === Math.max(...chatMessages.map(o => o.chatID)));
  }

  private latestChatId(chatMessages: ChatMessagesHasView[])
  {
    if ((!chatMessages) ||
        (chatMessages.length === 0))
    {
      return ChatID.UNKNOWN_CHAT_ID;
    }
    /*
      The response of a message with chatType withOptions has the same chatID as the message, so in order to get this response,
      we need to decrement 1 to the chatId in order to get this response message
    */
    else if (chatMessages[0].type === ChatMsgType.withOptions)
    {
      return chatMessages.reduce((max, current) => Math.max(max, current.chatID - 1), chatMessages[0].chatID - 1);
    }

    return chatMessages.reduce((max, current) => Math.max(max, current.chatID), chatMessages[0].chatID);
  }

  public getReplies(driverID: number)
  {
    return this.chatMailService.operatorGetDriverReplies(this.userService.getJWT(), driverID, this.getDespatchGroups())
    .pipe(
      map(resp =>
      {
        return resp;
      }),
    );
  }

  public getMessagesFromStorage(driverID: number): ChatMessagesHasView[]
  {
    return JSON.parse(localStorage.getItem(LocalStorageItem.DRIVER + driverID));
  }

  public saveMessagesInStorage(msgs: ChatMessagesHasView[], driverID: number)
  {
    if (driverID)
    {
      localStorage.setItem(LocalStorageItem.DRIVER + driverID, JSON.stringify(msgs));
    }
  }

  public getOldRecentChats(): Conversation[]
  {
    return JSON.parse(localStorage.getItem(LocalStorageItem.RECENT_CHATS));
  }

  public getRecentChats()
  {
    return JSON.parse(localStorage.getItem(LocalStorageItem.OPERATORS_RECENT_CHATS));
  }

  public setRecentChats(chats: Conversation[])
  {
    localStorage.setItem(LocalStorageItem.OPERATORS_RECENT_CHATS, JSON.stringify(this.getCurrOpRecentChatsUpdated(chats)));
  }

  private getCurrOpRecentChatsUpdated(chats: Conversation[])
  {
    let currRecentChats = this.getRecentChats();
    let shouldAddNewChatToList = true;


    if ((currRecentChats) &&
        (currRecentChats.length))
    {
      currRecentChats = currRecentChats.map(groupedMsg =>
      {
        if (groupedMsg.id === this.userService.getOperatorName())
        {
          shouldAddNewChatToList = false;
          return {...groupedMsg, chats};
        }

        return groupedMsg;

      });


      if (shouldAddNewChatToList)
      {
        currRecentChats.push({id: this.userService.getOperatorName(), chats});
      }

      return currRecentChats;
    }

    return [{id: this.userService.getOperatorName(), chats}]
  }

  public setDespatchGroups(despatchGroups: number[])
  {
    this.despatchGroups = despatchGroups;
  }

  public getDespatchGroups(): number[]
  {
    return this.despatchGroups;
  }

  public getChatsFilteredByDespGroup()
  {
    return this.chatsFilteredByDespGroup;
  }

  public setFilteredChatsByDespGroup()
  {
    if (this.getRecentChats())
    {
      let currOpRecentChats = this.getRecentChats().find(chat => chat.id === this.userService.getOperatorName());
      this.chatsFilteredByDespGroup = currOpRecentChats ? currOpRecentChats.chats : [];

      if ((this.despatchGroups.length) &&
          (this.chatsFilteredByDespGroup.length))
      {
        this.chatsFilteredByDespGroup = this.chatsFilteredByDespGroup.filter(chat => (this.despatchGroups.includes(chat.despatchGroupID)) || 
                                                                                     (chat.despatchGroupID === 0))
      }
    }
  }

  handleError(error: TranslatableError)
  {
    let currError;
    let errorMsg;

    if (error.translatableMessage)
    {
      currError = error.translatableMessage;
    }

    switch (error.errorType)
    {
      case ErrorType.UNHANDLED:
      {
        // here because:
        //   - 'access' field existing in response
        //   - or due to comms error
        //   - or 403 error,
        // so determine if it is 403 so we can retry after refreshing jwt...
        if ((error as any).status === 403)
        {
          errorMsg = this.translateService.instant('IDS_SESSION_EXPIRED');
        }
        break;
      }
    }

    if (currError)
    {
      currError.toPromise().then(errorText =>
      {
        if (!errorMsg)
        {
          errorMsg = errorText;
        }
        this.broadcaster.broadcast(BroadcasterEvent.EVENT_SHOW_CHAT_ERROR, errorMsg);
      });
    }
  }
}
